// @ts-strict-ignore
import React, { useState, useRef, useEffect } from 'react'
import Flickity, { FlickityOptions } from 'react-flickity-component'
import { isRTL } from 'mobile_web/modules/platform'
import { IonFooter, IonGrid, IonModal, IonRow, IonText } from '@ionic/react'
import Button from '../Button'
import { AccessDataType, AccessTypes, ItemConfigType } from './types'
import strings from 'mobile_web/strings'
import './styles.less'
import { checkStoredPermissions, skipPermissionsModal } from './functions'

interface Props {
	types: AccessTypes[]
	onCheckDone?: () => void
}

const AccessModal = ({ types, onCheckDone }: Props): JSX.Element => {
	const sliderRef = useRef<Flickity>(null)
	const [didPresent, setDidPresent] = useState<boolean>(false)
	const [isOpenModal, setIsOpen] = useState<boolean>(false)
	const [isStored, setStored] = useState<boolean>(false)

	useEffect(() => {
		;(async () => {
			const isExist = await checkStoredPermissions(types)
			setStored(isExist)
		})()
	}, [])

	const options: FlickityOptions = {
		prevNextButtons: false,
		rightToLeft: isRTL(),
		pageDots: types.length > 1,
	}

	const itemConfig: Record<string, ItemConfigType> = {
		camera: {
			icon: 'compass',
			title: strings.permission_modify_camera_title,
			description: strings.camera_access_needed,
		},
		location: {
			icon: 'map-marked-alt',
			title: strings.permission_modify_location_title,
			description: strings.location_access_needed,
		},
	}

	useEffect(() => {
		checkAccess()
			.then((res: AccessDataType) => {
				types.forEach((item, index) => {
					if (!res[item]) {
						setIsOpen(true)
					}

					if (types.length === index + 1 && onCheckDone && !!res[item]) {
						onCheckDone()
					}
				})
			})
			.catch()
	}, [])

	const checkAccess = () =>
		new Promise(async resolve => {
			const positionAccess = types.includes('location') ? await getLocationAccess() : null
			const cameraAccess = types.includes('camera') ? await getCameraAccess() : null

			resolve({ location: positionAccess, camera: cameraAccess })
		})

	const getCameraAccess = async () =>
		new Promise(resolve => {
			if (!navigator?.mediaDevices && !navigator?.mediaDevices?.getUserMedia) {
				;(navigator as any).userMedia = (navigator as any)?.mozGetUserMedia || (navigator as any)?.getUserMedia

				if (!(navigator as any)?.userMedia) {
					resolve(false)
					return
				} else {
					;(navigator as any)
						.userMedia({
							video: true,
						})
						.then(() => resolve(true))
						.catch(() => resolve(false))
				}
			}
			navigator?.mediaDevices
				?.getUserMedia({ video: true })
				.then(() => resolve(true))
				.catch(() => resolve(false))
		})

	const getLocationAccess = () =>
		new Promise(resolve => {
			navigator?.geolocation?.getCurrentPosition(
				() => resolve(true),
				() => resolve(false),
			)
		})

	const generateSliderItems = (): ItemConfigType[] => types.map((type: AccessTypes) => itemConfig[type])

	const closeModal = (): void => {
		if (onCheckDone) onCheckDone()
		setIsOpen(false)
	}

	const onSkipPermission = () => {
		skipPermissionsModal(types)
		setIsOpen(false)
	}

	const renderItems = (): JSX.Element[] => {
		const items = generateSliderItems() || []

		return items.map((item, index) => (
			<div key={index}>
				<IonGrid className="content">
					<i className={`icon fal fa-${item.icon}`} />
					<IonRow>
						<h2>{item.title}</h2>
					</IonRow>
					<IonRow>
						<IonText>{item.description}</IonText>
					</IonRow>
				</IonGrid>
				<IonFooter className="ion-no-border">
					<Button fill="clear" onClick={onSkipPermission}>
						{strings.do_not_show_me_again}
					</Button>
					{items.length === index + 1 ? (
						<Button onClick={closeModal} fill="clear" className="action-button" ariaLabel={strings.ok}>
							{strings.ok}
						</Button>
					) : (
						<Button
							onClick={() => sliderRef.current.next()}
							fill="clear"
							className="action-button"
							ariaLabel={strings.tour_next}
						>
							{strings.tour_next}
						</Button>
					)}
				</IonFooter>
			</div>
		))
	}

	const onSwiperRef = (ref: Flickity) => {
		sliderRef.current = ref
	}

	return (
		<IonModal
			isOpen={isOpenModal && !isStored}
			className="access-modal"
			onDidPresent={() => setTimeout(() => setDidPresent(true), 0)}
			onWillDismiss={() => setDidPresent(false)}
			backdropDismiss={false}
		>
			{didPresent && (
				<Flickity flickityRef={onSwiperRef} options={options}>
					{renderItems()}
				</Flickity>
			)}
		</IonModal>
	)
}

export default AccessModal
